/**
 * Centralized Copy/Text Content
 *
 * All user-facing text for the BDUDS application.
 * Organized by feature/component area for easy maintenance.
 *
 * Usage:
 *   import { COPY } from '@/copy';
 *   <h1>{COPY.app.title}</h1>
 *   <p>{COPY.home.tagline}</p>
 */

export const COPY = {
  // ============================================================================
  // APP-LEVEL
  // ============================================================================
  app: {
    title: 'Battleduds',
    tagline: 'Got more to lose than ships?',
    footer: {
      license: '© 2024 BDUDS. All rights reserved.',
    },
    notFound: {
      title: '404 - Page Not Found',
      message: 'The page you are looking for does not exist.',
      action: 'Go Home',
    },
  },

  // ============================================================================
  // HOME PAGE
  // ============================================================================
  home: {
    welcome: {
      title: 'Welcome to Battleduds',
      subtitle: 'The classic game with real stakes',
    },
    buttons: {
      inviteOpponent: 'Invite an Opponent',
      startNewGame: 'Start New Game',
      rejoinGame: 'Rejoin Game',
    },
    howToPlay: {
      title: 'How to Play',
      steps: [
        {
          number: 1,
          title: 'Create a Game',
          description: 'Click "Start New Game" to create a new game room.',
        },
        {
          number: 2,
          title: 'Invite Your Opponent',
          description: 'Share the game link with your opponent to join.',
        },
        {
          number: 3,
          title: 'Enter Your Prizes (Optional)',
          description:
            'Each player can secretly enter up to 3 prizes. If you capture the bonus piece and win, you spin the prize wheel!',
        },
        {
          number: 4,
          title: 'Place Your Pieces',
          description: 'Place your T, S, O, L, and Bonus pieces on your grid.',
        },
        {
          number: 5,
          title: 'Ready Up',
          description: 'Click "Play" when your pieces are placed.',
        },
        {
          number: 6,
          title: 'Take Turns',
          description: 'Take turns firing shots to find and destroy opponent pieces.',
        },
        {
          number: 7,
          title: 'Win the Game',
          description: 'Destroy all opponent pieces to win. Capture the bonus piece to spin for prizes!',
        },
      ],
    },
    warningModal: {
      title: 'Active Game Detected',
      message: 'You have an active game in progress. Starting a new game will leave your current game.',
      confirm: 'Start New Game',
      cancel: 'Cancel',
    },
  },

  // ============================================================================
  // GAME PHASES & STATUS
  // ============================================================================
  phase: {
    setup: {
      title: 'Setup Phase',
      message: 'Place your pieces on the grid',
      emoji: '🎯',
    },
    waiting: {
      title: 'Waiting',
      message: 'Waiting for opponent',
      emoji: '⏳',
    },
    playing: {
      title: 'Battle in Progress',
      message: 'Fire away!',
      emoji: '💥',
    },
    gameOver: {
      titleWin: 'You Won!',
      titleLose: 'You Lost',
      messageWin: 'Victory is yours!',
      messageLose: 'Better luck next time',
      emojiWin: '🎉',
      emojiLose: '😢',
    },
  },

  turn: {
    yourTurn: 'Your Turn',
    opponentTurn: "Opponent's Turn",
  },

  ready: {
    bothReady: 'Both players ready! Game starting...',
    waitingForOpponent: 'Waiting for opponent to ready up',
    opponentReady: 'Opponent is ready! Click Play when ready',
    readyCount: (current, total) => `${current}/${total} players ready`,
  },

  // ============================================================================
  // MODALS & POPUPS
  // ============================================================================
  modal: {
    invite: {
      title: 'Invite an Opponent',
      instructions: 'Share this link with a friend to start playing',
      copyButton: 'Copy Link',
      copyButtonCopied: 'Copied!',
      close: 'Close',
    },

    waiting: {
      waitingToJoin: 'Waiting for opponent to join...',
      waitingToReady: 'Waiting for opponent to be ready...',
      waitingToPlay: 'Waiting for opponent to click Play...',
    },

    victory: {
      titleWin: 'You Win!',
      titleLose: 'You Lose',
      messageWin: 'Congratulations! You destroyed all opponent pieces.',
      messageWinWithBonus:
        'Congratulations! You destroyed all opponent pieces and captured the bonus piece!',
      messageLose: 'Better luck next time! Your opponent destroyed all your pieces.',
      messageLoseWithBonus:
        'Better luck next time! Your opponent destroyed all your pieces and captured the bonus piece.',
      prizeWheelResult: 'Prize Wheel Result:',
      buttons: {
        close: 'Close',
        newGame: 'New Game',
        playAgain: 'Play Again',
      },
    },

    countdown: {
      getReady: 'Get Ready!',
      gameStarting: 'Game Starting!',
      numbers: ['3', '2', '1', 'Go!'],
    },
  },

  // ============================================================================
  // PIECE PLACEMENT
  // ============================================================================
  placement: {
    buttons: {
      rotate: 'Rotate ↻',
      play: 'Play',
      ready: 'Ready!',
      placeAll: (placed, total) => `Place All Pieces (${placed}/${total})`,
    },
    hints: {
      placeAll: 'Place all your pieces on the grid to start',
      waitingForOpponent: "You're ready! Waiting for opponent...",
    },
    feedback: {
      piecesPlaced: (placed, total) => `Pieces Placed: ${placed}/${total}`,
      allPlaced: 'All pieces placed! Click Play when ready.',
    },
    rotation: {
      degrees: (deg) => `${deg}°`,
    },
    errors: {
      // These are dynamically generated in the component
    },
  },

  // ============================================================================
  // PRIZE SYSTEM
  // ============================================================================
  prizes: {
    entry: {
      title: 'Enter Your Bonus Prizes',
      instructions:
        'Enter up to 3 prizes. If you capture the bonus piece AND win the game, the winner spins the prize wheel to claim one prize from the pool!',
      labels: {
        prize1: 'Prize 1',
        prize2: 'Prize 2 (Optional)',
        prize3: 'Prize 3 (Optional)',
        required: '*',
      },
      placeholders: {
        prize1: 'e.g., Back rub',
        prize2: 'e.g., Pick dinner',
        prize3: 'e.g., Movie night choice',
      },
      characterCounter: (current, max) => `${current}/${max}`,
      validation: {
        required: 'You must enter at least 1 prize',
        tooLong: (num, max) => `Prize ${num} must be ${max} characters or less`,
        wrongPhase: 'You can only submit prizes during the setup phase',
        submitFailed: 'Failed to submit prizes. Please try again.',
      },
      success: 'Prizes submitted successfully!',
      buttons: {
        submit: 'Submit Prizes',
        update: 'Update Prizes',
        submitting: 'Submitting...',
      },
      locked: "Prizes locked after clicking 'Ready to Play'",
    },

    display: {
      title: 'Prize Pool',
      yourPrizes: 'Your Prizes',
      opponentPrizes: "Opponent's Prizes",
      emptyState: 'No prizes entered yet',
      secretState: "Your opponent's entries are secret until the game begins.",
      bonusRule:
        'Win the game AND capture the bonus piece to spin the wheel and claim a prize!',
    },

    wheel: {
      titles: {
        ready: 'Ready to Spin!',
        readyAlt: 'Prize Wheel Ready!',
        spinning: 'Prize Wheel Spinning!',
        complete: 'The Wheel Has Spoken!',
      },
      labels: {
        prizePool: 'Prize Pool:',
        deciding: 'Fate is deciding...',
      },
      buttons: {
        spin: 'Spin the Wheel!',
      },
      waiting: 'Waiting for winner to spin...',
      result: {
        announcement: 'The prize is:',
        winner: 'You captured the bonus piece and won this prize!',
        loser: 'You must give this prize to the winner!',
      },
    },
  },

  // ============================================================================
  // PIECE DESTRUCTION & BONUS CAPTURE
  // ============================================================================
  pieceDestroyed: {
    title: 'Piece Destroyed!',
    message: (playerLabel, pieceType) => `${playerLabel} lost their ${pieceType} Piece!`,
    buttons: {
      dismiss: 'Dismiss',
    },
    flavorText: {
      // 1 piece lost
      low: [
        'Uh oh!',
        'A piece falls!',
        'The tide turns!',
        'First blood!',
        'Direct hit!',
        'Gotcha!',
        'One down!',
        'Nice shot!',
        'It begins!',
        'Taking damage!',
        'Ouch!',
        'That hurt!',
        'They found one!',
        'Contact confirmed!',
        'A crack in the armor!',
        'The hunt is on!',
      ],
      // 2 pieces lost
      medium: [
        'Pressure mounting!',
        'Can you recover?',
        'Things heating up!',
        'The stakes rise!',
        'Momentum shifting!',
        'Trouble brewing!',
        'Getting serious!',
        'No turning back!',
        'The tables turn!',
        'Walls closing in!',
        'Dangerous territory!',
        'Feeling the heat!',
        'The squeeze is on!',
      ],
      // 3+ pieces lost
      high: [
        'On the brink!',
        'Critical damage!',
        'Backs against the wall!',
        'Desperate times!',
        'Hanging by a thread!',
        'Last stand!',
        'All hope fading!',
        'The end draws near!',
        'Final moments!',
        'Barely hanging on!',
        'Catastrophic losses!',
        'Total devastation!',
        'Almost nothing left!',
        'The final push!',
      ],
    },
  },

  bonusCaptured: {
    title: 'Bonus Piece Captured!',
    message: (playerLabel) => `${playerLabel} captured the bonus piece!`,
    eligibility: (playerLabel) =>
      `If ${playerLabel} wins, they'll spin the prize wheel!`,
    buttons: {
      close: 'Close',
    },
    badge: {
      text: 'BONUS',
      ariaLabel: 'Bonus piece captured',
    },
  },

  // ============================================================================
  // SCOREBOARD
  // ============================================================================
  scoreboard: {
    you: 'You:',
    opponent: 'Opponent:',
    piecesLost: (lost, total) => `${lost}/${total}`,
  },

  // ============================================================================
  // ERROR HANDLING
  // ============================================================================
  errors: {
    boundary: {
      title: 'Something Went Wrong',
      types: {
        network: {
          message:
            'Unable to connect to the game server. This could be a temporary network issue.',
          action: 'Check your connection and try again',
        },
        timeout: {
          message: 'The connection to the game server timed out.',
          action: 'Try again',
        },
        session: {
          message:
            'Your game session has expired or become invalid. This can happen if the game has been inactive for a while.',
          action: 'Start a new game',
        },
        generic: {
          message:
            'An unexpected error occurred. This might be a temporary issue with the game.',
          action: 'Try refreshing the page or starting a new game',
        },
      },
      buttons: {
        tryAgain: 'Try Again',
        goHome: 'Go Home',
      },
      devDetails: 'Error Details (Development)',
    },

    handler: {
      invalidSession: 'This game session link is invalid. Please check the link and try again.',
      expiredSession:
        'This game session has expired (sessions last 24 hours). Please create a new game.',
      fullGame:
        'This game already has two players and is full. Please create a new game.',
      connectionLost: 'Connection to the game server was lost. Attempting to reconnect...',
      networkProblem:
        'Network connection problem. Please check your internet connection and try again.',
      unexpected: 'An unexpected error occurred. Please try again or create a new game.',
    },

    message: {
      close: 'Close message',
    },
  },

  // ============================================================================
  // LOADING & ACCESSIBILITY
  // ============================================================================
  loading: {
    default: 'Loading',
    withText: (text) => `Loading: ${text}`,
  },

  accessibility: {
    // Screen reader announcements and aria-labels
    pieceButton: (pieceName) => `Place ${pieceName} piece`,
    closeButton: 'Close',
    dismissButton: 'Dismiss',
  },

  // ============================================================================
  // PIECE DEFINITIONS
  // ============================================================================
  pieces: {
    T: { name: 'T', icon: '⊤' },
    S: { name: 'S', icon: '⌇' },
    O: { name: 'O', icon: '▢' },
    L: { name: 'L', icon: '⌊' },
    Bonus: { name: 'Bonus', icon: '★' },
  },

  // ============================================================================
  // PLAYER LABELS
  // ============================================================================
  players: {
    player1: 'Player 1',
    player2: 'Player 2',
  },
};

/**
 * Helper function to get player label from player ID
 */
export function getPlayerLabel(playerId) {
  return playerId === 'player1' ? COPY.players.player1 : COPY.players.player2;
}

/**
 * Helper function to get piece icon
 */
export function getPieceIcon(pieceType) {
  return COPY.pieces[pieceType]?.icon || '◆';
}

/**
 * Helper function to select random flavor text based on pieces lost
 */
export function selectFlavorText(piecesLostCount) {
  const { low, medium, high } = COPY.pieceDestroyed.flavorText;

  let texts;
  if (piecesLostCount <= 1) {
    texts = low;
  } else if (piecesLostCount === 2) {
    texts = medium;
  } else {
    texts = high;
  }

  return texts[Math.floor(Math.random() * texts.length)];
}
